import { useLayoutEffect, useRef, useEffect } from "react";
import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  CSS2DRenderer,
  CSS2DObject,
} from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { AudioContext as ThreeAudioContext } from "three/src/audio/AudioContext.js";

// three r15x 계열은 AudioContext 유틸이 존재합니다. (프로젝트 버전에 따라 존재하지 않을 수 있음)
// 없으면 아래 줄은 주석 처리하세요.
// const ThreeAudioContext = THREE.AudioContext || THREE.getAudioContext?.();

export default function ThreeViewer({
  plyUrl, // 로드할 PLY 모델 URL
  textureUrl, // 메시에 적용할 텍스처 URL
  ambience, // 앰비언스(코너/핫스팟) 사운드 설정
  isPlay, // 재생/정지 상태
  mouseMode, // "move" | "pointer" : 조작 모드
  isCameraReset, // "reset" 일 때 카메라/메시 상태 초기화 트리거
  setIsCameraReset, // 리셋 플래그 해제 콜백
  onAnalysisReady, // WebAudio 분석기 준비 완료 콜백
  onDistanceChange, // 카메라-대상 거리(m) 변경 콜백
  onClickSpectrum, // 스펙트럼(라이브/스냅) 전송 콜백
  setIsNearHotspot,
}) {
  // ───────────────────────────────────────────────────────────
  // Refs (런타임 상태/객체 저장)
  // ───────────────────────────────────────────────────────────
  const mountRef = useRef(null); // 렌더러를 부착할 DOM 컨테이너
  const sceneRef = useRef(null); // THREE.Scene 인스턴스
  const groupRef = useRef(null); // 메시/마커 등이 포함된 그룹 루트
  const cameraRef = useRef(null); // 카메라
  const rendererRef = useRef(null); // WebGLRenderer
  const labelRendererRef = useRef(null); // CSS2DRenderer (마커 UI)

  const analyserRef = useRef(null); // WebAudio AnalyserNode
  const listenerRef = useRef(null); // THREE.AudioListener

  const meshRef = useRef(null); // 로드된 메시
  const controlsRef = useRef(null); // OrbitControls

  const audioLoaderRef = useRef(null); // THREE.AudioLoader
  const bufferCacheRef = useRef(new Map()); // 오디오 버퍼 캐시

  const ambienceRigRef = useRef(null); // 앰비언스 리그(노드/사운드/설정)

  const pointerPxRef = useRef({ x: NaN, y: NaN }); // 캔버스 로컬 좌표의 포인터 px
  const pointerNdcRef = useRef(new THREE.Vector2()); // NDC 좌표 캐시
  const raycasterRef = useRef(new THREE.Raycaster()); // 레이캐스터

  const hoverRef = useRef(false); // 캔버스 Hover 상태
  const nearHotspotRef = useRef(false);
  const meshHoverRef = useRef(false); // 레이캐스트로 메시 위 Hover 여부

  const markerRef = useRef(null); // 선택 마커 { el, obj }
  const holdVolRef = useRef(NaN); // 마커로 고정된 핫스팟 볼륨
  const hotspotVolRef = useRef(NaN); // 마지막 적용된 핫스팟 볼륨

  const isPlayRef = useRef(false); // 재생 상태 캐시(이펙트/루프용)
  const modeRef = useRef(mouseMode); // 현재 마우스 모드 캐시
  const spacePressedRef = useRef(false); // 스페이스(Translate 전환) 눌림 상태

  const heatCursorRef = useRef(null);
  const heatLabelRef = useRef(null);
  const rafRef = useRef(0); // requestAnimationFrame id
  const initWorldDistRef = useRef(1); // 초기 카메라-타깃 월드 거리
  const initialCameraPositionRef = useRef(new THREE.Vector3(0, 0, -1));
  const initialMeshStateRef = useRef(null); // 메시 초기 변환 정보

  const spectrumFpsRef = useRef(10); // 라이브 FFT 전송 FPS 제한
  const lastSpectrumTsRef = useRef(0); // 마지막 FFT 전송 시간

  // ───────────────────────────────────────────────────────────
  // Helpers (주요 유틸 함수)
  // ───────────────────────────────────────────────────────────

  function clearMarker() {
    // 현재 마커 객체/DOM 제거
    if (!markerRef.current) return;
    const { obj, el } = markerRef.current;
    obj?.parent?.remove?.(obj);
    el?.remove?.();
    markerRef.current = null;
  }

  function getMeshHalfExtents(mesh) {
    // 메시의 바운딩박스 기반 절반 폭/높이 반환 (스케일 반영)
    const geo = mesh.geometry;
    geo.computeBoundingBox();
    const bb = geo.boundingBox;
    const sx = mesh.scale.x,
      sy = mesh.scale.y;
    const hx = (bb.max.x - bb.min.x) * 0.5 * sx;
    const hy = (bb.max.y - bb.min.y) * 0.5 * sy;
    return { hx, hy };
  }

  async function loadBuffer(url) {
    // 오디오 버퍼 로드 + 캐싱
    if (!url) return null;
    const cache = bufferCacheRef.current;
    if (cache.has(url)) return cache.get(url);
    const buf = await audioLoaderRef.current.loadAsync(encodeURI(url));
    cache.set(url, buf);
    return buf;
  }

  async function setupOrUpdateMeshAmbience() {
    // 메시 주변 코너/핫스팟 사운드 리그 생성/업데이트
    const cam = cameraRef.current;
    const mesh = meshRef.current;
    const listener = listenerRef.current;
    if (!cam || !mesh || !listener) return;

    const urls = ambience?.urls || {};
    const getUrl = (k) => urls[k] || urls.all || null;

    const zFrac = ambience?.zFrac ?? -0.5; // 카메라 기준 z 위치 비율
    const expand = ambience?.expand ?? 0.05; // 사운드 노드 확장 비율

    // 리그 초기 생성
    if (!ambienceRigRef.current) {
      const nodes = {
        tl: new THREE.Object3D(),
        tr: new THREE.Object3D(),
        bl: new THREE.Object3D(),
        br: new THREE.Object3D(),
        hotspot: new THREE.Object3D(),
      };
      for (const k of Object.keys(nodes)) mesh.add(nodes[k]);
      ambienceRigRef.current = { nodes, sounds: {}, urls: {}, zFrac, expand };
    } else {
      // 기존 리그 갱신
      ambienceRigRef.current.zFrac = zFrac;
      ambienceRigRef.current.expand = expand;
      ambienceRigRef.current.urls = ambienceRigRef.current.urls || {};
    }

    const rig = ambienceRigRef.current;

    // 각 키(tl/tr/bl/br/hotspot)에 대응하는 사운드 준비/교체/볼륨 초기화
    const ensureSoundForKey = async (k, url) => {
      const wantPositional = k === "hotspot"; // 핫스팟만 3D PositionalAudio
      let snd = rig.sounds[k];

      const needReplace =
        !snd ||
        (wantPositional && !(snd instanceof THREE.PositionalAudio)) ||
        (!wantPositional && !(snd instanceof THREE.Audio));

      // 타입 다르면 교체
      if (needReplace) {
        if (snd) {
          try {
            if (snd.isPlaying) snd.stop();
          } catch {}
          try {
            rig.nodes?.[k]?.remove(snd);
          } catch {}
        }
        snd = wantPositional
          ? new THREE.PositionalAudio(listener)
          : new THREE.Audio(listener);
        rig.nodes[k].add(snd);
        rig.sounds[k] = snd;
      }

      // 핫스팟 거리감 설정
      if (wantPositional) {
        const hs = ambience?.hotspot || {};
        const distanceModel =
          hs.distanceModel ?? ambience?.distanceModel ?? "exponential";
        const refDistance = hs.refDistance ?? ambience?.refDistance ?? 1;
        const maxDistance = hs.maxDistance ?? ambience?.maxDistance ?? 5.0;
        const rolloff = hs.rolloff ?? ambience?.rolloff ?? 1.0;

        snd.setPanningModel?.("HRTF");
        snd.setDistanceModel?.(distanceModel);
        snd.setRefDistance?.(refDistance);
        snd.setMaxDistance?.(maxDistance);
        snd.setRolloffFactor?.(rolloff);
      }

      // URL 없으면 버퍼만 비움
      if (!url) {
        try {
          if (snd.isPlaying) snd.stop();
        } catch {}
        try {
          snd.setBuffer?.(null);
        } catch {}
        rig.urls[k] = null;
        return;
      }

      // 버퍼 로드/적용 + 자동재생(재생 상태일 때)
      const prevUrl = rig.urls?.[k];
      if (prevUrl !== url || !snd.buffer) {
        try {
          const buf = await loadBuffer(url);
          if (buf) {
            try {
              if (snd.isPlaying) snd.stop();
            } catch {}
            snd.setBuffer(buf);
            snd.setLoop(true);
            rig.urls[k] = url;

            if (isPlayRef.current) {
              try {
                const ctx = listener.context;
                if (ctx?.state === "suspended") await ctx.resume();
                if (!snd.isPlaying) snd.play();
              } catch {}
            }
          }
        } catch (e) {
          console.error(`[ambience:${k}] load error`, e);
        }
      }

      // 코너 기본 볼륨(핫스팟은 프레임마다 계산)
      const gains = {
        tl: 0.3,
        tr: 0.3,
        bl: 0.3,
        br: 0.3,
        hotspot: 1.0,
        ...(ambience?.gains || {}),
      };
      if (k !== "hotspot") {
        try {
          snd.setVolume(gains[k] ?? 0.5);
        } catch {}
      }
    };

    // 코너 사운드/핫스팟 준비
    for (const k of ["tl", "tr", "bl", "br"])
      await ensureSoundForKey(k, getUrl(k));
    await ensureSoundForKey("hotspot", getUrl("hotspot"));

    syncAmbiencePlayState(); // 재생/정지 상태 동기화
    updateHotspotVolumeByPointer(); // 커서 기반 초기 볼륨 반영
  }

  const _tmp = new THREE.Vector3(); // 임시 벡터 캐시
  function updateMeshAmbienceNodes() {
    // 카메라/메시 상태를 반영해 리그 노드 위치 갱신
    const cam = cameraRef.current;
    const mesh = meshRef.current;
    const rig = ambienceRigRef.current;
    if (!cam || !mesh || !rig) return;

    const { hx, hy } = getMeshHalfExtents(mesh);
    const ex = hx * (1 + (rig.expand ?? 0));
    const ey = hy * (1 + (rig.expand ?? 0));

    const camLocal = _tmp.copy(cam.position);
    mesh.worldToLocal(camLocal);
    const z = (rig.zFrac ?? 0.5) * camLocal.z;

    rig.nodes.tl.position.set(-ex, +ey, z);
    rig.nodes.tr.position.set(+ex, +ey, z);
    rig.nodes.bl.position.set(-ex, -ey, z);
    rig.nodes.br.position.set(+ex, -ey, z);

    // 핫스팟 위치(기본값 [5,-2,0])
    const hotspotPosInput = ambience?.hotspot?.pos ?? [5, -2, 0];
    if (Array.isArray(hotspotPosInput)) {
      rig.nodes.hotspot.position.set(
        hotspotPosInput[0],
        hotspotPosInput[1],
        hotspotPosInput[2],
      );
    } else {
      rig.nodes.hotspot.position.set(
        hotspotPosInput.x ?? 0,
        hotspotPosInput.y ?? 0,
        hotspotPosInput.z ?? 0,
      );
    }
  }

  function syncAmbiencePlayState() {
    // 앰비언스 사운드들의 재생/정지 상태를 isPlayRef에 맞춰 동기화
    const rig = ambienceRigRef.current;
    const listener = listenerRef.current;
    if (!rig || !listener) return;
    const wantPlay = !!isPlayRef.current;
    for (const k in rig.sounds) {
      const snd = rig.sounds[k];
      if (!snd?.buffer) continue;
      try {
        if (wantPlay) {
          if (!snd.isPlaying) snd.play();
        } else {
          if (snd.isPlaying) snd.stop();
        }
      } catch {}
    }
  }

  // 포인터 ↔ 핫스팟 스크린 거리로 목표 게인 계산
  function computeHotspotGainForClientXY(clientX, clientY) {
    const rig = ambienceRigRef.current;
    const cam = cameraRef.current;
    const renderer = rendererRef.current;
    if (!rig || !cam || !renderer) return 0;
    const node = rig.nodes?.hotspot;
    const snd = rig.sounds?.hotspot;
    if (!node || !snd) return 0;

    const world = new THREE.Vector3();
    node.getWorldPosition(world);
    const ndc = world.clone().project(cam);
    const rect = renderer.domElement.getBoundingClientRect();
    const sx = (ndc.x * 0.5 + 0.5) * rect.width;
    const sy = (-ndc.y * 0.5 + 0.5) * rect.height;

    const d = Math.hypot(clientX - rect.left - sx, clientY - rect.top - sy);
    const NEAR = 40,
      FAR = 400;
    const baseGain = ambience?.gains?.hotspot ?? 1.0;
    const minGain = 0.0;
    let t = (FAR - d) / (FAR - NEAR);
    t = Math.max(0, Math.min(1, t));
    const s = t * t * (3 - 2 * t); // smoothstep
    return minGain + (baseGain - minGain) * s;
  }

  function updateHotspotVolumeByPointer() {
    // 포인터 위치(또는 마커 고정값)에 맞춰 핫스팟 볼륨 적용
    const rig = ambienceRigRef.current;
    const cam = cameraRef.current;
    const renderer = rendererRef.current;
    if (!rig || !cam || !renderer) return;

    const snd = rig.sounds?.hotspot;
    const node = rig.nodes?.hotspot;
    if (!node) return;

    let target;
    let near = false;
    if (markerRef.current && Number.isFinite(holdVolRef.current)) {
      // 마커 고정 시 고정 볼륨 사용
      target = holdVolRef.current;
    } else {
      // 포인터-핫스팟 거리 기반 볼륨
      const px = pointerPxRef.current.x,
        py = pointerPxRef.current.y;
      if (Number.isFinite(px) && Number.isFinite(py)) {
        const world = new THREE.Vector3();
        node.getWorldPosition(world);
        const ndc = world.clone().project(cam);
        const rect = renderer.domElement.getBoundingClientRect();
        const sx = (ndc.x * 0.5 + 0.5) * rect.width;
        const sy = (-ndc.y * 0.5 + 0.5) * rect.height;
        const d = Math.hypot(px - sx, py - sy);
        const NEAR = 30,
          FAR = 400;
        const baseGain = ambience?.gains?.hotspot ?? 1.0;
        const minGain = 0.0;
        let t = (FAR - d) / (FAR - NEAR);
        t = Math.max(0, Math.min(1, t));
        const s = t * t * (3 - 2 * t);
        target = minGain + (baseGain - minGain) * s;
        near = d <= NEAR;
      } else {
        // 포인터 정보 없을 때 기본 게인
        target = ambience?.gains?.hotspot ?? 1.0;
      }
    }

    // Reuse the audio gain, without sampling pixels or triggering React renders.
    const cursor = heatCursorRef.current;
    if (cursor) {
      const visible = modeRef.current === "pointer" && hoverRef.current && meshHoverRef.current;
      cursor.hidden = !visible;
      cursor.classList.toggle("awaiting-play", !isPlayRef.current);
      if (visible) {
        const base = ambience?.gains?.hotspot ?? 1;
        const strength = base > 0 ? Math.max(0, Math.min(1, target / base)) : 0;
        // Only reveal warmer colors as the signal grows: blue/green, yellow, then red.
        const hue = strength < 0.33
          ? 180 - (strength / 0.33) * 60
          : strength < 0.67
            ? 60
            : 60 * (1 - (strength - 0.67) / 0.33);
        const center = `hsl(${hue} 82% 58%)`;
        cursor.style.transform = `translate3d(${pointerPxRef.current.x}px, ${pointerPxRef.current.y}px, 0)`;
        cursor.style.setProperty("--heat-color", center);
        cursor.style.setProperty("--heat-size", `${52 + strength * 13}px`);
        cursor.style.setProperty("--heat-gradient", `radial-gradient(circle, ${center} 0%, ${center} 28%, hsl(${Math.min(190, hue + 60)} 80% 58%) 44%, #37c9df 53%, #477deb 62%, #477deb00 72%)`);
        cursor.classList.toggle("label-left", pointerPxRef.current.x > renderer.domElement.clientWidth - 180);
        cursor.classList.toggle("label-above", pointerPxRef.current.y > renderer.domElement.clientHeight - 65);
        if (heatLabelRef.current) {
          const level = strength < 0.33 ? "약함" : strength < 0.67 ? "보통" : "강함";
          heatLabelRef.current.textContent = isPlayRef.current
            ? `${level} · ${Math.round(strength * 100)}%${markerRef.current ? " · 고정" : ""}`
            : "음원을 재생하세요";
        }
      }
    }

    if (
      !Number.isFinite(hotspotVolRef.current) ||
      Math.abs(target - hotspotVolRef.current) > 0.01
    ) {
      try {
        snd?.setVolume?.(target);
      } catch {}
      hotspotVolRef.current = target;
    }
    nearHotspotRef.current = near;
    if (typeof setIsNearHotspot === "function") {
      setIsNearHotspot(near);
    }
  }

  function emitSpectrum(kind = "live") {
    // 분석기에서 FFT 데이터 읽어 콜백으로 전달 (라이브/스냅)
    const a = analyserRef.current;
    const l = listenerRef.current;
    if (!a || !l || typeof onClickSpectrum !== "function") return;
    const byteData = new Uint8Array(a.frequencyBinCount);
    a.getByteFrequencyData(byteData);
    const bins = new Float32Array(a.frequencyBinCount);
    a.getFloatFrequencyData(bins);
    // Capture distance with the bins so a pinned spectrum retains its weighting.
    let hotspotDistance = Infinity;
    const hotspot = ambienceRigRef.current?.nodes?.hotspot;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    if (hotspot && camera && renderer) {
      const projected = hotspot.getWorldPosition(new THREE.Vector3()).project(camera);
      const rect = renderer.domElement.getBoundingClientRect();
      const { x, y } = pointerPxRef.current;
      if (Number.isFinite(x) && Number.isFinite(y)) {
        hotspotDistance = Math.hypot(
          x - (projected.x * 0.5 + 0.5) * rect.width,
          y - (-projected.y * 0.5 + 0.5) * rect.height,
        );
      }
    }
    onClickSpectrum({
      byteData,
      bins,
      fftSize: a.fftSize,
      sampleRate: l.context.sampleRate,
      ts: performance.now(),
      minDecibels: a.minDecibels,
      maxDecibels: a.maxDecibels,
      isNearHotspot: nearHotspotRef.current,
      hotspotDistance,
      kind, // "live" | "snap"
    });
  }

  // ───────────────────────────────────────────────────────────
  // Effects (상태 동기화/수명주기)
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    isPlayRef.current = !!isPlay;
  }, [isPlay]); // props → ref 동기화
  useEffect(() => {
    syncAmbiencePlayState();
  }, [isPlay]); // 재생/정지 반영

  useEffect(() => {
    // 모드 전환 시: 커서/컨트롤/마커 상태 초기화
    modeRef.current = mouseMode;
    if (heatCursorRef.current) heatCursorRef.current.hidden = true;
    clearMarker();
    holdVolRef.current = NaN;
    hotspotVolRef.current = NaN;

    if (controlsRef.current) controlsRef.current.enabled = mouseMode === "move";
    const mount = mountRef.current;
    const cvs = mount?.querySelector("canvas");
    if (cvs) {
      if (mouseMode === "move") {
        cvs.style.cursor = "grab";
      } else if (mouseMode === "pointer") {
        cvs.style.cursor = "crosshair";
      } else {
        cvs.style.cursor = "default";
      }
    }
  }, [mouseMode]);

  // 초기 셋업 (씬/카메라/렌더러/컨트롤/오디오/이벤트/루프)
  useLayoutEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const { clientWidth: w, clientHeight: h } = mount;

    // 오디오 컨텍스트 생성 및 three에 연결(가능 시)
    const AC = window.AudioContext || window["webkitAudioContext"];
    if (!AC) {
      console.error("This browser does not support Web Audio API");
      return;
    }
    const desiredCtx = new AC({ sampleRate: 96000 });
    try {
      ThreeAudioContext?.setContext?.(desiredCtx);
    } catch {}
    audioLoaderRef.current = new THREE.AudioLoader();

    // Scene/Camera
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    sceneRef.current = scene;

    const group = new THREE.Group(); // 메시/마커 등 자식 보관
    scene.add(group);
    groupRef.current = group;

    const camera = new THREE.PerspectiveCamera(60, w / h, 0.01, 1000);
    camera.position.set(0, 0, -1);
    cameraRef.current = camera;

    // Audio Listener & Analyser
    const listener = new THREE.AudioListener();
    camera.add(listener);
    listenerRef.current = listener;

    const ctx = listener.context;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;
    listener.getInput().connect(analyser);
    analyserRef.current = analyser;

    // Boost only speaker output; keep the analyser's original signal unchanged.
    const playbackGain = ctx.createGain();
    playbackGain.gain.value = 3;
    const playbackCompressor = ctx.createDynamicsCompressor();
    playbackCompressor.threshold.value = -3;
    playbackCompressor.knee.value = 3;
    playbackCompressor.ratio.value = 12;
    playbackCompressor.attack.value = 0.003;
    playbackCompressor.release.value = 0.15;
    listener.getInput().disconnect(ctx.destination);
    listener.getInput().connect(playbackGain);
    playbackGain.connect(playbackCompressor);
    playbackCompressor.connect(ctx.destination);

    // 분석기 준비 알림
    onAnalysisReady?.({
      context: ctx,
      analyser,
      spec: {
        fftSize: analyser.fftSize,
        smoothingTimeConstant: analyser.smoothingTimeConstant,
        minDecibels: analyser.minDecibels,
        maxDecibels: analyser.maxDecibels,
        sampleRate: ctx.sampleRate,
      },
    });

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    mount.appendChild(renderer.domElement);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // 포인터 좌표 추적 + 메시 호버 판단(포인터 모드일 때)
    const onPointerMoveTrack = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointerPxRef.current.x = e.clientX - rect.left;
      pointerPxRef.current.y = e.clientY - rect.top;

      if (modeRef.current === "pointer") {
        const { x, y } = pointerPxRef.current;
        const ndcX = (x / rect.width) * 2 - 1;
        const ndcY = -(y / rect.height) * 2 + 1;
        pointerNdcRef.current.set(ndcX, ndcY);
        raycasterRef.current.setFromCamera(pointerNdcRef.current, camera);
        const hits = raycasterRef.current.intersectObject(group, true);
        meshHoverRef.current = hits.length > 0;
      } else {
        meshHoverRef.current = false;
      }
    };

    // CSS2DRenderer (마커용)
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(w, h);
    labelRenderer.domElement.style.position = "absolute";
    labelRenderer.domElement.style.top = "0";
    labelRenderer.domElement.style.left = "0";
    labelRenderer.domElement.style.pointerEvents = "none";
    mount.appendChild(labelRenderer.domElement);
    labelRendererRef.current = labelRenderer;

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(-0.1, 0.0, -0.1);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.near = 0.01;
    key.shadow.camera.far = 10;
    key.shadow.normalBias = 0.02;
    scene.add(key);

    const fill = new THREE.DirectionalLight(0xffffff, 1);
    fill.position.set(0, 0.0, -0.1);
    scene.add(fill);

    // OrbitControls (기본 이동/줌, 회전/팬 제한)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 0.5;
    controls.maxDistance = 10;
    controls.target.set(0, 0, 0);
    controls.enableRotate = false;
    controls.enablePan = false;
    controls.enableZoom = true;
    controls.touches.ONE = THREE.TOUCH.NONE;
    controls.enabled = modeRef.current === "move";
    controls.update();
    controlsRef.current = controls;

    // A finite floor, separate from the pickable model and its audio nodes.
    let floorGrid = null;
    const floorBounds = new THREE.Box3();
    const floorCenter = new THREE.Vector3();
    const updateFloor = () => {
      if (!floorGrid || !meshRef.current) return;
      floorBounds.setFromObject(meshRef.current);
      floorBounds.getCenter(floorCenter);
      floorGrid.position.set(floorCenter.x, floorBounds.min.y - 0.015, floorCenter.z);
    };

    // Texture + PLY 로드
    const texture = new THREE.TextureLoader().load(textureUrl);
    new PLYLoader().load(
      plyUrl,
      (geometry) => {
        // 정상 로드 시 메시 생성/정규화
        geometry.computeVertexNormals();
        geometry.center();

        const material = new THREE.MeshStandardMaterial({
          map: texture,
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geometry, material);

        // 최대 치수 기준 정규화 스케일
        const box = new THREE.Box3().setFromObject(mesh);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 1.0 / (maxDim || 1);
        mesh.scale.setScalar(scale);

        mesh.rotateZ(Math.PI);
        mesh.castShadow = true;
        group.add(mesh);

        meshRef.current = mesh;
        const floorSize = Math.max(size.x, size.z) * scale * 1.25;
        floorGrid = new THREE.GridHelper(floorSize, 20, 0x7fa7da, 0xcbdcf0);
        floorGrid.material.transparent = true;
        floorGrid.material.opacity = 0.8;
        scene.add(floorGrid);
        updateFloor();
        // Leave a margin around the finite grid, including its nearest edge.
        const halfHeight = Math.max(size.y * scale * 0.5, Math.abs(floorGrid.position.y));
        const fitDistance = floorSize * 0.5 + Math.max(floorSize * 0.5 / camera.aspect, halfHeight) / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * 1.15;
        camera.position.set(0, 0, -fitDistance * 0.6);
        initialCameraPositionRef.current.copy(camera.position);
        controls.update();
        initialMeshStateRef.current = {
          position: mesh.position.clone(),
          rotation: mesh.rotation.clone(),
          scale: mesh.scale.clone(),
        };

        // 초기 카메라-메시 중심 거리 저장(거리 매핑용 앵커)
        try {
          const center = new THREE.Vector3();
          mesh.getWorldPosition(center);
          initWorldDistRef.current = camera.position.distanceTo(center) || 1.0;
        } catch {}

        setupOrUpdateMeshAmbience(); // 메시가 준비된 뒤 앰비언스 구성
      },
      undefined,
      (err) => console.error("PLY load error:", err),
    );

    // 드래그/번역 유틸(그룹 회전/메시 평행이동)
    const camX = new THREE.Vector3();
    const camY = new THREE.Vector3();
    const camZ = new THREE.Vector3();
    const worldDelta = new THREE.Vector3();
    const tmpWorldPos = new THREE.Vector3();

    const rotateGroup = (dx, dy, speed = 0.0009) => {
      // 마우스 드래그로 그룹 회전
      group.rotateY(dx * speed);
      group.rotateX(-dy * speed);
    };

    const translateMeshByPixels = (dx, dy) => {
      // 화면 픽셀 이동량을 월드 이동량으로 환산하여 메시 이동
      const cam = cameraRef.current;
      const mesh = meshRef.current;
      if (!cam || !mesh) return;

      mesh.getWorldPosition(tmpWorldPos);
      const distance = cam.position.distanceTo(tmpWorldPos);
      const vFov = THREE.MathUtils.degToRad(cam.fov);
      const worldPerPixelY =
        (2 * Math.tan(vFov / 2) * distance) / renderer.domElement.clientHeight;
      const worldPerPixelX = worldPerPixelY * cam.aspect;

      const moveX = dx * worldPerPixelX;
      const moveY = dy * worldPerPixelY;

      cam.matrixWorld.extractBasis(camX, camY, camZ);
      worldDelta
        .copy(camX)
        .multiplyScalar(moveX)
        .add(camY.multiplyScalar(-moveY));

      const parent = mesh.parent ?? sceneRef.current;
      mesh.getWorldPosition(tmpWorldPos);
      tmpWorldPos.add(worldDelta);
      parent.worldToLocal(tmpWorldPos);
      mesh.position.copy(tmpWorldPos);
    };

    // 포인터 드래그 상태/모드 관리
    let dragging = false;
    let lastX = 0,
      lastY = 0;
    let dragMode = "rotate";

    renderer.domElement.style.touchAction = "none";

    const onPointerDown = (e) => {
      // move 모드에서 드래그 시작(회전/번역)
      if (modeRef.current !== "move") return;
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      dragMode = spacePressedRef.current ? "translate" : "rotate";
      renderer.domElement.style.cursor =
        dragMode === "translate" ? "move" : "grabbing";
      controls.enabled = false;
      renderer.domElement.setPointerCapture?.(e.pointerId);
    };

    const onPointerMoveDrag = (e) => {
      // 드래그 중 동작(스페이스로 모드 전환 가능)
      if (!dragging) return;
      const desired = spacePressedRef.current ? "translate" : "rotate";
      if (desired !== dragMode) {
        dragMode = desired;
        renderer.domElement.style.cursor =
          dragMode === "translate" ? "move" : "grabbing";
      }
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      if (dragMode === "translate") translateMeshByPixels(dx, dy);
      else rotateGroup(dx, dy);
    };

    const onPointerUp = (e) => {
      // 드래그 종료
      if (!dragging) return;
      dragging = false;
      renderer.domElement.style.cursor = "grab";
      controls.enabled = modeRef.current === "move";
      renderer.domElement.releasePointerCapture?.(e.pointerId);
    };

    const onWheel = (e) => {
      // move 모드에서 브라우저 스크롤 방지
      if (modeRef.current !== "move") return;
      e.preventDefault();
    };

    // 마커 생성/토글 도우미
    const ensureMarkerAtHit = (hit) => {
      // 교차 지점에 CSS2D 마커 생성/부착
      if (markerRef.current) return;
      const offset = 0.0005 * (meshRef.current?.scale.x || 1);
      let marker = markerRef.current;
      if (!marker) {
        const el = document.createElement("span");
        el.style.cssText = `
         width: 36px; height: 36px;
         border-radius: 50%;
         border: 3px solid #ff5252;
         background: rgba(255,82,82,0.2);
         pointer-events: none;
         transform: translate(-50%, -50%);
         box-sizing: border-box;
       `;
        const obj = new CSS2DObject(el);
        marker = { el, obj };
        markerRef.current = marker;
      }
      const localPoint = hit.object.worldToLocal(hit.point.clone());
      marker.obj.position.copy(localPoint);
      if (hit.face) {
        const n = hit.face.normal.clone().normalize();
        marker.obj.position.add(n.multiplyScalar(offset));
      }
      if (marker.obj.parent !== hit.object) {
        marker.obj.parent?.remove?.(marker.obj);
        hit.object.add(marker.obj);
      }
    };

    const projectToScreen = (v3, out, cam, rect) => {
      // 월드 좌표 → 스크린 px 변환
      const ndc = v3.clone().project(cam);
      out.x = (ndc.x * 0.5 + 0.5) * rect.width;
      out.y = (-ndc.y * 0.5 + 0.5) * rect.height;
      return out;
    };

    // 클릭: 마커 토글 + FFT 스냅/라이브 전환
    const onClick = (e) => {
      if (modeRef.current !== "pointer") return;

      const rect = renderer.domElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // 1) 마커가 이미 있으면 근접 클릭으로 해제
      if (markerRef.current) {
        const world = new THREE.Vector3();
        markerRef.current.obj.getWorldPosition(world);
        const sp = { x: 0, y: 0 };
        projectToScreen(world, sp, camera, rect);
        const d = Math.hypot(sp.x - x, sp.y - y);

        if (d <= 10) {
          clearMarker();
          holdVolRef.current = NaN; // 볼륨 고정 해제
          emitSpectrum("live"); // 라이브 모드 알림
        }
        // 다른 곳 클릭 시 스냅샷 갱신 금지
        return;
      }

      // 2) 마커 없을 때 히트 지점에 생성 + 스냅샷 고정
      const ndcX = (x / rect.width) * 2 - 1;
      const ndcY = -(y / rect.height) * 2 + 1;
      pointerNdcRef.current.set(ndcX, ndcY);
      raycasterRef.current.setFromCamera(pointerNdcRef.current, camera);
      const hits = raycasterRef.current.intersectObject(group, true);

      if (hits.length) {
        ensureMarkerAtHit(hits[0]);
        holdVolRef.current = computeHotspotGainForClientXY(
          e.clientX,
          e.clientY,
        );
        emitSpectrum("snap"); // 스냅 고정 알림
      } else {
        clearMarker();
        holdVolRef.current = NaN;
        emitSpectrum("live");
      }
    };

    // 이벤트 등록
    renderer.domElement.addEventListener("pointermove", onPointerMoveTrack);
    renderer.domElement.addEventListener("wheel", onWheel, { passive: false });
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMoveDrag);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("pointerleave", onPointerUp);
    renderer.domElement.addEventListener("click", onClick);

    const onEnter = () => {
      hoverRef.current = true;
    }; // Hover 시작
    const onLeave = () => {
      hoverRef.current = false;
      if (heatCursorRef.current) heatCursorRef.current.hidden = true;
    }; // Hover 종료
    renderer.domElement.addEventListener("mouseenter", onEnter);
    renderer.domElement.addEventListener("mouseleave", onLeave);

    // 스페이스로 드래그 모드(Translate) 보조
    const onKeyDown = (e) => {
      if ((e.code === "Space" || e.key === " ") && modeRef.current === "move") {
        spacePressedRef.current = true;
        if (hoverRef.current) e.preventDefault();
      }
    };
    const onKeyUp = (e) => {
      if (e.code === "Space" || e.key === " ") spacePressedRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown, { passive: false });
    window.addEventListener("keyup", onKeyUp);

    // Render Loop (컨트롤/사운드/거리/스펙트럼 업데이트 + 렌더)
    const renderLoop = () => {
      controls.update();
      updateFloor();
      updateMeshAmbienceNodes();
      updateHotspotVolumeByPointer(); // 커서 기반 핫스팟 볼륨

      // 카메라-메시 중심 거리(m) 계산 → onDistanceChange
      try {
        const cam = cameraRef.current;
        const mesh = meshRef.current;
        const ctrls = controlsRef.current;
        if (cam && mesh && ctrls && typeof onDistanceChange === "function") {
          const center = new THREE.Vector3();
          mesh.getWorldPosition(center);
          const w = cam.position.distanceTo(center); // world 단위
          const wMin = Math.max(1e-6, ctrls.minDistance ?? 0.5);
          const wMax = Math.max(wMin + 1e-6, ctrls.maxDistance ?? 10);
          const wMid = Math.max(
            wMin,
            Math.min(wMax, initWorldDistRef.current ?? 1.0),
          );
          const mMin = 0.3,
            mMid = 1.0,
            mMax = 3.0;
          let meters;
          if (w <= wMid) {
            const t = (w - wMin) / Math.max(1e-6, wMid - wMin);
            meters = mMin + t * (mMid - mMin);
          } else {
            const t = (w - wMid) / Math.max(1e-6, wMax - wMid);
            meters = mMid + t * (mMax - mMid);
          }
          meters = Math.min(mMax, Math.max(mMin, meters));
          const prev = renderLoop.__lastMeters;
          if (!Number.isFinite(prev) || Math.abs(prev - meters) >= 0.01) {
            onDistanceChange(Number(meters.toFixed(2)));
            renderLoop.__lastMeters = meters;
          }
        }
      } catch {}

      // 포인터 모드 + 재생 중 + 메시 hover + 마커 없음 → 라이브 FFT 전송(프레임 제한)
      if (
        modeRef.current === "pointer" &&
        isPlayRef.current &&
        meshHoverRef.current &&
        !markerRef.current
      ) {
        const now = performance.now();
        const budget = 1000 / Math.max(1, spectrumFpsRef.current);
        if (now - lastSpectrumTsRef.current >= budget) {
          emitSpectrum("live");
          lastSpectrumTsRef.current = now;
        }
      }

      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(renderLoop);
    };
    rafRef.current = requestAnimationFrame(renderLoop);

    // Resize 처리
    const onResize = () => {
      const newW = mount.clientWidth || 1;
      const newH = mount.clientHeight || 1;
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
      renderer.setSize(newW, newH);
      labelRenderer.setSize(newW, newH);
      updateMeshAmbienceNodes();
    };
    window.addEventListener("resize", onResize);

    // Cleanup (이벤트/리소스 해제)
    return () => {
      clearMarker();
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);

      renderer.domElement.removeEventListener(
        "pointermove",
        onPointerMoveTrack,
      );
      renderer.domElement.removeEventListener("wheel", onWheel);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMoveDrag);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("pointerleave", onPointerUp);
      renderer.domElement.removeEventListener("click", onClick);
      renderer.domElement.removeEventListener("mouseenter", onEnter);
      renderer.domElement.removeEventListener("mouseleave", onLeave);

      // 앰비언스 리그 정리
      const rig = ambienceRigRef.current;
      if (rig) {
        for (const k of ["tl", "tr", "bl", "br", "hotspot"]) {
          try {
            if (rig.sounds?.[k]?.isPlaying) rig.sounds[k].stop();
          } catch {}
          try {
            rig.nodes?.[k]?.remove(rig.sounds?.[k]);
          } catch {}
          try {
            meshRef.current?.remove(rig.nodes?.[k]);
          } catch {}
        }
        ambienceRigRef.current = null;
      }

      controls.dispose();
      floorGrid?.geometry.dispose();
      floorGrid?.material.dispose();

      if (renderer.domElement?.parentNode === mount)
        mount.removeChild(renderer.domElement);
      if (labelRendererRef.current) {
        mount.removeChild(labelRendererRef.current.domElement);
        labelRendererRef.current = null;
      }

      // 지오메트리/머티리얼 해제
      try {
        const objs = [];
        group.traverse((c) => objs.push(c));
        objs.forEach((c) => {
          if (c.isMesh) {
            c.geometry?.dispose?.();
            (Array.isArray(c.material) ? c.material : [c.material]).forEach(
              (m) => m?.dispose?.(),
            );
          }
          scene.remove(c);
        });
      } catch {}

      try {
        camera.remove(listener);
      } catch {}
      listenerRef.current = null;

      try {
        listener.getInput()?.disconnect?.(analyser);
      } catch {}
      analyserRef.current = null;

      listener.getInput().disconnect(playbackGain);
      playbackGain.disconnect();
      playbackCompressor.disconnect();

      try {
        desiredCtx.suspend?.();
      } catch {}
    };
  }, [plyUrl, textureUrl]);

  // 앰비언스 설정 변경 시 리그 갱신
  useEffect(() => {
    setupOrUpdateMeshAmbience();
  }, [ambience]);

  // Reset 트리거 처리 (카메라/그룹/메시/마커/컨트롤 초기화)
  useEffect(() => {
    if (isCameraReset !== "reset") return;
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const mesh = meshRef.current;
    const initial = initialMeshStateRef.current;
    const group = groupRef.current;
    if (!cam) return;

    cam.position.copy(initialCameraPositionRef.current);
    cam.quaternion.set(0, 0, 0, 1);
    cam.updateProjectionMatrix();

    if (group) {
      group.rotation.set(0, 0, 0);
      group.scale.set(1, 1, 1);
    }
    if (mesh && initial) {
      mesh.position.copy(initial.position);
      mesh.rotation.copy(initial.rotation);
      mesh.scale.copy(initial.scale);
    }

    clearMarker();
    holdVolRef.current = NaN;
    hotspotVolRef.current = NaN;

    if (controls) {
      controls.target.set(0, 0, 0);
      controls.update();
    }

    renderer?.render(scene, cam);
    setIsCameraReset?.("");
  }, [isCameraReset, setIsCameraReset]);

  return <div ref={mountRef} className="sample-viewer-mount">
    <div ref={heatCursorRef} className="sample-heat-cursor" hidden aria-hidden="true">
      <span className="sample-heat-halo" /><span className="sample-heat-reticle" />
      <span className="sample-heat-label"><small>상대 강도</small><b ref={heatLabelRef} /></span>
    </div>
  </div>;
}
